import { useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { ListFieldsType } from "src/api/config";
import { myTaskMeetingsListName } from "src/api/config/meetings.config";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import DashboardLayout from "src/layouts/DashboardLayout";
import RoutesLayout from "src/layouts/RouteLayout";
import { getInitData } from "src/modules/initData/actions";
import { getItemsRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { selectCurrentUser } from "src/modules/users/selector";
import { systemRoutes } from "src/routes/routes";
import { isMobile } from "src/utils/device";
import { MeetingCard } from "./mytasks/meetingsPage/components/MeetingCard";
import { MeetingCardMobile } from "./mytasks/meetingsPage/components/MeetingCardMobile";
import ActionCard from "./throughcomponents/actionCard";
import ActionCardMobile from "./throughcomponents/actionCard/actionCard.mobile";
import AuditCard from "./throughcomponents/auditCard";
import AuditCardMobile from "./throughcomponents/auditCard/auditCard.mobile";
import Card5Way from "./throughcomponents/card5Way";
import Card5WayMobile from "./throughcomponents/card5Way/card5Way.mobile";
import DocumentCard from "./throughcomponents/documetnCard";
import DocumentCardMobile from "./throughcomponents/documetnCard/documentCardMobileNew";
import IncidentCard from "./throughcomponents/incidentCard";
import IncidentCardMobile from "./throughcomponents/incidentCard/incidentCard.mobile";
import SecurityCard from "./throughcomponents/securityCard";
import { FiveWhyType } from "src/api/config/systemSwitch";
import { TaskCard } from "./mytasks/checkList/TaskCard.mobile";
import { selectChecklistPeriodDate } from "src/modules/checklistTasks";
import { NotificationReportDrawer } from "src/components/tables/NotifyReportTableWithFilters";

const ServicesPage = () => {
  const { id = "1" } = useParams();
  let systemRootRoutes = systemRoutes[id] ?? systemRoutes["1"];

  return (
    <DashboardLayout>
      <RoutesLayout routes={systemRootRoutes} />
      <AllModal />
    </DashboardLayout>
  );
};

export default ServicesPage;

function isHasAccessToMeeting(
  meeting: ListFieldsType[typeof myTaskMeetingsListName],
  currentUser: { isAdmin: boolean; email: string }
) {
  if (meeting.isPrivate) {
    return (
      meeting.Owners.includes(currentUser.email) ||
      (meeting.Participants && meeting.Participants.includes(currentUser.email))
    );
  } else {
    return true;
  }
}

function AllModal() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const checkListPeriodDate = useAppSelector(selectChecklistPeriodDate);

  const [documentCardOpen, setDocumentCardOpen] = useState(false);
  const [documentCardMobileOpen, setDocumentCardMobileOpen] = useState(false);
  const [meetingCardMobileOpen, setMeetingCardMobileOpen] = useState(false);
  const [meetingCardOpen, setMeetingCardOpen] = useState(false);
  const [taskCardOpen, setTaskCardOpen] = useState(false);
  const [actionCardOpen, setActionCardOpen] = useState(false);
  const [actionCardMobileOpen, setActionCardMobileOpen] = useState(false);
  const [auditCardOpen, setAuditCardOpen] = useState(false);
  const [auditCardMobileOpen, setAuditCardMobileOpen] = useState(false);
  const [incidentCardOpen, setIncidentCardOpen] = useState(false);
  const [incidentCardMobileOpen, setIncidentCardMobileOpen] = useState(false);
  const [securityCardOpen, setSecurityCardOpen] = useState(false);
  const [card5WayOpen, setCard5WayOpen] = useState(false);
  const [card5WayMobileOpen, setCard5WayMobileOpen] = useState(false);
  const [deviationCardOpen, setDeviationCardOpen] = useState(false);
  const [deviationCardMobileOpen, setDeviationCardMobileOpen] = useState(false);

  const [query, setQuery] = useSearchParams();
  const [closeAllModals, setCloseAllModals] = useState(false);
  const resolvedActionCardMobileIDForRender =
    new URLSearchParams(location.search).get("actionCardMobile") ||
    (new URLSearchParams(location.search).get("auditCardMobile")
      ? new URLSearchParams(location.search).get("actionCard")
      : null);

  useEffect(() => {
    dispatch(getInitData());
  }, []);

  const dialogToggle = (b: boolean) => {
    setDocumentCardOpen(b);
    setDocumentCardMobileOpen(b);
    setAuditCardOpen(b);
    setAuditCardMobileOpen(b);
    setIncidentCardOpen(b);
    setIncidentCardMobileOpen(b);
    setSecurityCardOpen(b);
    query.delete("documentCard");
    query.delete("documentCardMobile");
    query.delete("notificationID");
    query.delete("version");
    query.delete("removeMail");
    query.delete("auditCard");
    query.delete("incidentCard");
    query.delete("incidentCardMobile");
    query.delete("auditCardMobile");
    query.delete("securityCard");
    query.delete("docFromNotification");
    setQuery(query);
  };

  const dialogActionCardToggle = (b: boolean) => {
    if (!b) query.delete("actionCard");
    setQuery(query);
    setActionCardOpen(b);
  };

  const dialogActionCardMobileToggle = (b: boolean) => {
    if (!b) {
      query.delete("actionCardMobile");
      query.delete("actionCard");
    }
    setQuery(query);
    setActionCardMobileOpen(b);
  };

  const dialogCard5WayToggle = (b: boolean) => {
    if (!b) query.delete("5WayCard");
    setQuery(query);
    setCard5WayOpen(b);
  };

  const dialogCard5WayMobileToggle = (b: boolean) => {
    if (!b) query.delete("5WayCardMobile");
    setQuery(query);
    setCard5WayMobileOpen(b);
  };

  const dialogDeviationCardToggle = (b: boolean) => {
    if (!b) query.delete("deviationCard");
    setQuery(query);
    setDeviationCardOpen(b);
  };

  const dialogDeviationCardMobileToggle = (b: boolean) => {
    if (!b) query.delete("deviationCardMobile");
    setQuery(query);
    setDeviationCardMobileOpen(b);
  };

  const dialogMeetingCardToggle = (b: boolean) => {
    if (!b) query.delete("meetingCard");
    setQuery(query);
    setMeetingCardOpen(b);
  };

  const dialogMeetingCardMobileToggle = (b: boolean) => {
    if (!b) query.delete("meetingCardMobile");
    setQuery(query);
    setMeetingCardMobileOpen(b);
  };

  const dialogTaskCardToggle = (b: boolean) => {
    if (!b) {
      query.delete("taskCard");
      query.delete("taskCardEmail");
    }
    setQuery(query);
    setTaskCardOpen(b);
  };

  useEffect(() => {
    const documentCardID = new URLSearchParams(location.search).get("documentCard");
    const documentCardMobileID = new URLSearchParams(location.search).get("documentCardMobile");
    const actionCardID = new URLSearchParams(location.search).get("actionCard");
    const actionCardMobileID = new URLSearchParams(location.search).get("actionCardMobile");
    const auditCardID = new URLSearchParams(location.search).get("auditCard");
    const auditCardMobileID = new URLSearchParams(location.search).get("auditCardMobile");
    const incidentCardID = new URLSearchParams(location.search).get("incidentCard");
    const incidentCardMobileID = new URLSearchParams(location.search).get("incidentCardMobile");
    const securityCardID = new URLSearchParams(location.search).get("securityCard");
    const card5WayID = new URLSearchParams(location.search).get("5WayCard");
    const card5WayMobileID = new URLSearchParams(location.search).get("5WayCardMobile");
    const deviationCardID = new URLSearchParams(location.search).get("deviationCard");

    const deviationCardMobileID = new URLSearchParams(location.search).get("deviationCardMobile");
    const taskCardID = new URLSearchParams(location.search).get("taskCard");

    if (isMobile() && documentCardID && !documentCardMobileID) {
      const newQuery = new URLSearchParams(location.search);
      newQuery.delete("documentCard");
      newQuery.set("documentCardMobile", documentCardID);
      setQuery(newQuery);
      return;
    }

    if (documentCardID) {
      setDocumentCardOpen(true);
    } else {
      setDocumentCardOpen(false);
    }

    if (documentCardMobileID) {
      setDocumentCardMobileOpen(true);
    } else {
      setDocumentCardMobileOpen(false);
    }

    const resolvedActionCardMobileID =
      actionCardMobileID || (auditCardMobileID && actionCardID ? actionCardID : null);

    if (actionCardID && !resolvedActionCardMobileID) {
      setActionCardOpen(true);
    } else {
      setActionCardOpen(false);
    }

    if (resolvedActionCardMobileID) {
      setActionCardMobileOpen(true);
    } else {
      setActionCardMobileOpen(false);
    }

    if (taskCardID) {
      setTaskCardOpen(true);
    } else {
      setTaskCardOpen(false);
    }

    if (auditCardID) {
      setAuditCardOpen(true);
    } else {
      setAuditCardOpen(false);
    }

    if (auditCardMobileID) {
      setAuditCardMobileOpen(true);
    } else {
      setAuditCardMobileOpen(false);
    }

    if (incidentCardID) {
      setIncidentCardOpen(true);
    } else {
      setIncidentCardOpen(false);
    }

    if (incidentCardMobileID) {
      setIncidentCardMobileOpen(true);
    } else {
      setIncidentCardMobileOpen(false);
    }

    if (securityCardID) {
      setSecurityCardOpen(true);
    } else {
      setSecurityCardOpen(false);
    }

    if (card5WayID) {
      setCard5WayOpen(true);
    } else {
      setCard5WayOpen(false);
    }

    if (card5WayMobileID) {
      setCard5WayMobileOpen(true);
    } else {
      setCard5WayMobileOpen(false);
    }

    if (deviationCardID) {
      setDeviationCardOpen(true);
    } else {
      setDeviationCardOpen(false);
    }

    if (deviationCardMobileID) {
      setDeviationCardMobileOpen(true);
    } else {
      setDeviationCardMobileOpen(false);
    }
  }, [location]);

  useAsyncEffect(async () => {
    const meetingCardID = new URLSearchParams(location.search).get("meetingCard");

    if (meetingCardID && currentUser.email) {
      const { items } = await getItemsRequest({ listName: myTaskMeetingsListName, filters: { ID: meetingCardID } });

      const [meetingObj] = modifiedItemsState(items, myTaskMeetingsListName);

      if (!isHasAccessToMeeting(meetingObj, currentUser)) {
        dialogMeetingCardToggle(false);
      } else {
        setMeetingCardOpen(true);
      }
    } else {
      setMeetingCardOpen(false);
    }
  }, [location, currentUser.email]);

  useAsyncEffect(async () => {
    const meetingCardMobileID = new URLSearchParams(location.search).get("meetingCardMobile");

    if (meetingCardMobileID && currentUser.email) {
      const { items } = await getItemsRequest({ listName: myTaskMeetingsListName, filters: { ID: meetingCardMobileID } });

      const [meetingObj] = modifiedItemsState(items, myTaskMeetingsListName);

      if (!isHasAccessToMeeting(meetingObj, currentUser)) {
        dialogMeetingCardMobileToggle(false);
      } else {
        setMeetingCardMobileOpen(true);
      }
    } else {
      setMeetingCardMobileOpen(false);
    }
  }, [location, currentUser.email]);

  useEffect(() => {
    if (documentCardOpen) {
      setDocumentCardOpen(false);
    }
    if (documentCardMobileOpen) {
      setDocumentCardMobileOpen(false);
    }
    if (actionCardOpen) {
      setActionCardOpen(false);
    }
    if (actionCardMobileOpen) {
      setActionCardOpen(false);
    }
    if (auditCardOpen) {
      setAuditCardOpen(false);
    }
    if (auditCardMobileOpen) {
      setAuditCardMobileOpen(false);
    }
    if (incidentCardOpen) {
      setIncidentCardOpen(false);
    }
    if (incidentCardMobileOpen) {
      setIncidentCardMobileOpen(false);
    }
    if (securityCardOpen) {
      setSecurityCardOpen(false);
    }
    if (card5WayOpen) {
      setCard5WayOpen(false);
    }
    if (card5WayMobileOpen) {
      setCard5WayMobileOpen(false);
    }
    if (deviationCardOpen) {
      setDeviationCardOpen(false);
    }

    if (deviationCardMobileOpen) {
      setDeviationCardMobileOpen(false);
    }

    if (taskCardOpen) {
      setTaskCardOpen(false);
    }
  }, [closeAllModals]);

  return (
    <>
      {documentCardOpen && (
        <DocumentCard
          open={documentCardOpen}
          closeAllModals={closeAllModals}
          setCloseAllModals={setCloseAllModals}
          toggleDialog={dialogToggle}
          documentCardID={new URLSearchParams(location.search).get("documentCard") && Number(new URLSearchParams(location.search).get("documentCard")) || null}
          versionView={new URLSearchParams(location.search).get("version") && Number(new URLSearchParams(location.search).get("version")) || null}
        />
      )}
      {documentCardMobileOpen && (
        <DocumentCardMobile
          open={documentCardMobileOpen}
          closeAllModals={closeAllModals}
          setCloseAllModals={setCloseAllModals}
          toggleDialog={dialogToggle}
          documentCardID={new URLSearchParams(location.search).get("documentCardMobile") && Number(new URLSearchParams(location.search).get("documentCardMobile")) || null}
          versionView={new URLSearchParams(location.search).get("version") && Number(new URLSearchParams(location.search).get("version")) || null}
        />
      )}
      {actionCardOpen && (
        <ActionCard
          open={actionCardOpen}
          setOpen={dialogActionCardToggle}
          zIndex={80}
          actionID={new URLSearchParams(location.search).get("actionCard")}
        />
      )}
      {actionCardMobileOpen && (
        <ActionCardMobile
          open={actionCardMobileOpen}
          setOpen={dialogActionCardMobileToggle}
          zIndex={80}
          actionID={resolvedActionCardMobileIDForRender}
        />
      )}

      {meetingCardOpen && (
        <MeetingCard
          open={meetingCardOpen}
          setOpen={dialogMeetingCardToggle}
          zIndex={50}
          meetingID={new URLSearchParams(location.search).get("meetingCard")}
        />
      )}

      {meetingCardMobileOpen && (
        <MeetingCardMobile
          open={meetingCardMobileOpen}
          setOpen={dialogMeetingCardMobileToggle}
          zIndex={50}
          meetingID={new URLSearchParams(location.search).get("meetingCardMobile")}
        />
      )}
      {taskCardOpen && (
        <TaskCard
          open={taskCardOpen}
          setOpen={dialogTaskCardToggle}
          zIndex={110}
          taskID={new URLSearchParams(location.search).get("taskCard")}
          periodDate={checkListPeriodDate}
        />
      )}
      {auditCardOpen && (
        <AuditCard
          open={auditCardOpen}
          setOpen={dialogToggle}
          zIndex={50}
          auditID={
            (new URLSearchParams(location.search).get("auditCard") &&
              Number(new URLSearchParams(location.search).get("auditCard"))) ||
            0
          }
        />
      )}
      {auditCardMobileOpen && (
        <AuditCardMobile
          open={auditCardMobileOpen}
          setOpen={dialogToggle}
          zIndex={50}
          auditID={
            (new URLSearchParams(location.search).get("auditCardMobile") &&
              Number(new URLSearchParams(location.search).get("auditCardMobile"))) ||
            0
          }
        />
      )}
      {incidentCardOpen && (
        <IncidentCard
          open={incidentCardOpen}
          setOpen={dialogToggle}
          zIndex={50}
          incidentID={
            (new URLSearchParams(location.search).get("incidentCard") &&
              Number(new URLSearchParams(location.search).get("incidentCard"))) ||
            0
          }
        />
      )}
      {incidentCardMobileOpen && (
        <IncidentCardMobile
          open={incidentCardMobileOpen}
          setOpen={dialogToggle}
          zIndex={50}
          incidentID={
            (new URLSearchParams(location.search).get("incidentCardMobile") &&
              Number(new URLSearchParams(location.search).get("incidentCardMobile"))) ||
            0
          }
        />
      )}
      {securityCardOpen && (
        <SecurityCard
          open={securityCardOpen}
          setOpen={dialogToggle}
          zIndex={50}
          incidentID={
            (new URLSearchParams(location.search).get("securityCard") &&
              Number(new URLSearchParams(location.search).get("securityCard"))) ||
            0
          }
        />
      )}
      {card5WayOpen && (
        <Card5Way
          type={FiveWhyType}
          open={card5WayOpen}
          setOpen={dialogCard5WayToggle}
          zIndex={50}
          card5WayID={
            (new URLSearchParams(location.search).get("5WayCard") &&
              Number(new URLSearchParams(location.search).get("5WayCard"))) ||
            0
          }
        />
      )}
      {card5WayMobileOpen && (
        <Card5WayMobile
          type={FiveWhyType}
          open={card5WayMobileOpen}
          setOpen={dialogCard5WayMobileToggle}
          zIndex={50}
          card5WayID={
            (new URLSearchParams(location.search).get("5WayCardMobile") &&
              Number(new URLSearchParams(location.search).get("5WayCardMobile"))) ||
            0
          }
        />
      )}
      {deviationCardOpen && (
        <Card5Way
          type="DeviationReport"
          open={deviationCardOpen}
          setOpen={dialogDeviationCardToggle}
          zIndex={50}
          card5WayID={
            (new URLSearchParams(location.search).get("deviationCard") &&
              Number(new URLSearchParams(location.search).get("deviationCard"))) ||
            0
          }
        />
      )}
      {deviationCardMobileOpen && (
        <Card5WayMobile
          type="DeviationReport"
          open={deviationCardMobileOpen}
          setOpen={dialogDeviationCardMobileToggle}
          zIndex={50}
          card5WayID={
            (new URLSearchParams(location.search).get("deviationCardMobile") &&
              Number(new URLSearchParams(location.search).get("deviationCardMobile"))) ||
            0
          }
        />
      )}
      {query.has("notificationCard") && (
        <NotificationReportDrawer zIndex={query.has("docFromNotification") ? 90 : 110} />
      )}
      {actionCardOpen && (
        <ActionCard
          open={actionCardOpen}
          setOpen={dialogActionCardToggle}
          zIndex={110}
          actionID={new URLSearchParams(location.search).get("actionCard")}
        />
      )}
    </>
  );
}
